import { ParsedUrlQuery } from 'querystring';
import { GetStaticPaths, GetStaticProps } from 'next';
import retry from 'async-retry';

import { getLayout } from 'components/layouts/Header';
import { OpportunityPage, OpportunityProps, opportunityPageStyles } from 'components/opportunity-page';
import { OpportunityResponse, TouchpointState } from 'lib/models/opportunity';
import { asyncRetryParams } from 'lib/consts';
import { pageBuilderInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { API_ROUTES } from 'lib/api-routes';
import { TouchpointsResponse } from 'lib/models/touchpoint';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { PAGE_ROUTES } from 'lib/page-routes';
import { isBefore1OCT2023 } from 'utils/time';

interface Params extends ParsedUrlQuery {
  id: string;
}

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return { paths: [], fallback: 'blocking' };
  }

  const { data: touchpointsResponse } = await pageBuilderInstance<TouchpointsResponse>(
    `${API_ROUTES.TOUCHPOINTS}/basic_index?touchpointable_type=Job&per_page=100000`
  );
  const touchpoints = parseArrayResponse(touchpointsResponse);
  const paths = touchpoints.map(({ slug }) => ({ params: { id: slug } }));
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<OpportunityProps, Params> = async (context) => {
  const slug = context.params?.id as string;
  const pathname = `${API_ROUTES.TOUCHPOINTS}/${slug}?with_details=true`;
  let propsData;

  try {
    propsData = await retry(async (bail) => {
      const { data } = await pageBuilderInstance<OpportunityResponse>(pathname);
      const opportunity = parseResponse(data);
      const { touchpointable_type, state, expired_at } = opportunity;

      if (touchpointable_type !== 'Job') {
        bail(new Error('Touchpoint is not a Job'));
        throw new Error('Touchpoint is not a Job');
      }

      const isExpiredBeforeDeadline = state === TouchpointState.EXPIRED && isBefore1OCT2023(expired_at);
      const canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/jobs/${slug}`;
      return { opportunity, canonicalURL, isExpiredBeforeDeadline };
    }, asyncRetryParams);
  } catch (error) {
    console.error(`Job page not found - ${slug}: ${error}`);
    return { notFound: true };
  }

  const { isExpiredBeforeDeadline, ...props } = propsData;

  if (isExpiredBeforeDeadline) {
    return {
      redirect: { destination: PAGE_ROUTES.JOBS_SEARCH, permanent: true },
    };
  }

  return { props: { ...props, ...(await fetchTranslationFiles({ locale: context.locale, pages: [LOCALE_PAGES.OPPORTUNITY] })) }, revalidate: 3600 };
};

// @ts-ignore
OpportunityPage.getLayout = getLayout.bind({ className: opportunityPageStyles.header });

export default OpportunityPage;
